import { Box, Button, Heading, Pill, Stack, Text, Tooltip } from '@sixfold/common-ui';
import { notNil } from '@sixfold/typed-primitives';
import { type ReactNode } from 'react';
import { Link } from 'react-router';

import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { customSlugify } from '../../lib/util/url';
import { Routes } from '../../routes';
import { HomeViewCompany, RecentTour, useFavoriteCompanies, useRecentCompanies, useRecentTours } from '../storage';

import styles from './home.module.css';

export function HomeContainer() {
  const recentTours = useRecentTours();
  const recentCompanies = useRecentCompanies();
  const [favoriteCompanies] = useFavoriteCompanies();
  const favoriteCompaniesHint =
    'Favorites are saved in this browser on your computer. Open a company page and click the star next to its name to favorite it. It will show up here next time you use backoffice in this browser, but not on another computer, browser, or if you clear your browser data.';

  const favoriteCompaniesTooltip = (
    <Tooltip content={favoriteCompaniesHint} placement="top">
      <Button
        type="button"
        kind="ghost"
        size="xsmall"
        hideLabel
        iconEnd="small-info-outline"
        className={styles.columnHint}>
        How favorites work
      </Button>
    </Tooltip>
  );

  return (
    <Stack>
      <HomeColumn title="Favorite companies" tooltip={favoriteCompaniesTooltip}>
        {favoriteCompanies.length === 0 ? (
          <Text prominence="subtle">No favorite companies yet</Text>
        ) : (
          favoriteCompanies.map((company) => <CompanyRow key={company.companyId} company={company} />)
        )}
      </HomeColumn>

      <HomeColumn title="Recently viewed companies">
        {recentCompanies.length === 0 ? (
          <Text prominence="subtle">No companies viewed yet</Text>
        ) : (
          recentCompanies.map((company) => <CompanyRow key={company.companyId} company={company} />)
        )}
      </HomeColumn>

      <HomeColumn title="Recently viewed tours">
        {recentTours.length === 0 ? (
          <Text prominence="subtle">No tours viewed yet</Text>
        ) : (
          recentTours.map((tour) => <TourRow key={tour.tourId} tour={tour} />)
        )}
      </HomeColumn>
    </Stack>
  );
}

function HomeColumn({ title, tooltip, children }: { title: string; tooltip?: ReactNode; children: ReactNode }) {
  return (
    <Box className={styles.column} padding="large" elevation="raised" radius="small">
      <Stack direction="row" alignItems="center" gap="xsmall" className={styles.columnHeader}>
        <Heading type="title" size="medium" className={styles.columnTitle}>
          {title}
        </Heading>
        {tooltip}
      </Stack>
      <Stack direction="column" gap="none" className={styles.list}>
        {children}
      </Stack>
    </Box>
  );
}

function TourRow({ tour }: { tour: RecentTour }) {
  const subtitle = tour.fromTo;

  return (
    <Link to={Routes.Tour.generatePath({ tour_id: tour.tourId.toString() })} className={styles.row}>
      <Stack direction="column" gap="none">
        <Text size="large">
          {`Tour id #${tour.tourId}`}
          {notNil(tour.shipperTransportNumber) && ` (${tour.shipperTransportNumber})`}
        </Text>
        {notNil(subtitle) && (
          <Text size="small" prominence="subtle">
            {subtitle}
          </Text>
        )}
        {notNil(tour.createdAt) && (
          <Text size="small" prominence="subtle">
            Created <FormattedDate date={tour.createdAt} showTime={false} showTimezone={false} />
          </Text>
        )}
        {(notNil(tour.shipperName) || notNil(tour.carrierName)) && (
          <div className={styles.rowParties}>
            {notNil(tour.shipperName) && <Pill kind="success">Shipper · {tour.shipperName}</Pill>}
            {notNil(tour.carrierName) && <Pill kind="info">Carrier · {tour.carrierName}</Pill>}
          </div>
        )}
      </Stack>
    </Link>
  );
}

function CompanyRow({ company }: { company: HomeViewCompany }) {
  return (
    <Link
      to={Routes.Company.generatePath({
        company_id: company.companyId,
        slug_company_name: customSlugify(company.companyName),
      })}
      className={styles.row}>
      <Text size="large">
        {company.companyName ?? 'Unknown company'}
        <span className={styles.rowMeta}>{` (#${company.companyId})`}</span>
      </Text>
    </Link>
  );
}
